import { cn } from '../../lib/styles';
import { HTMLAttributes, memo } from 'react';
import { Loader } from '../Loader';

type NumberBadgeCircleProps = HTMLAttributes<HTMLDivElement> & {
  /**
   * If true, the badge circle will be shown regardless of the children.
   * By default, the badge circle returns null when the children is falsy.
   */
  show?: boolean;
};

const NumberBadgeCircleBase = ({ className, children, show }: NumberBadgeCircleProps) => {
  const shouldHideBadge = !show && (!children || children === '0');
  if (shouldHideBadge) {
    return null;
  }
  const numberLength = typeof children === 'number' || typeof children === 'string' ? children.toString().length : 0;
  return (
    <div
      className={cn(
        'rounded-[16px] font-body-xs text-gray-1 bg-red-7 w-fit min-w-4 h-4 flex items-center justify-center',
        {
          'px-1': numberLength > 1,
        },
        className,
      )}
    >
      {children}
    </div>
  );
};

export const NumberBadgeCircle = memo(NumberBadgeCircleBase);

type NumberBadgeProps = HTMLAttributes<HTMLDivElement> & {
  /**
   * Value to be displayed inside the badge.
   */
  value?: React.ReactNode;
  /**
   * Class name override for the badge.
   * Position is best customized with translate.
   */
  badgeClassName?: string;
  /**
   * If true, the badge will be shown regardless of the value.
   * By default, the badge is hidden when the value is falsy.
   */
  show?: boolean;

  loading?: boolean;
};

const NumberBadgeBase = ({
  className,
  children,
  value,
  badgeClassName: badgeClassNameProp,
  show,
  loading,
}: NumberBadgeProps) => {
  const shouldHideBadge = !show && (!value || value === '0');

  const badgeClassName = cn('absolute left-full top-0 -translate-x-[6px] -translate-y-[4px]', badgeClassNameProp);

  if (shouldHideBadge) return children;

  return (
    <div className={cn('relative', className)}>
      {children}

      <NumberBadgeCircle show={show} className={badgeClassName}>
        {loading ? <Loader className={cn('size-2 min-w-2')} inverted /> : value}
      </NumberBadgeCircle>
    </div>
  );
};

export const NumberBadge = memo(NumberBadgeBase);
